import { QuoteGetResponse, QuoteSearchResponse, ReportingQuoteSource } from '@lio/api-types';
import { ArrayItem } from '@utils/commonTypes';

export type Quote = {
  name: string;
  id: string;
  address: string;
  state: string;
  city: string;
  zipcode: string;
  product: string;
  policy: string;
};

export type TQuoteApiResponse = ArrayItem<QuoteGetResponse>;

export type QuoteItem = ArrayItem<QuoteSearchResponse['items']>;

export enum QuoteType {
  Cancelation = 'Cancelation',
  Endorsement = 'Endorsement',
  NewBusiness = 'NewBusiness',
  Reinstatement = 'Reinstatement',
  Renewal = 'Renewal',
  Rewrite = 'Rewrite',
  Indication = 'Indication',
}

export const QuoteTypeLabels = {
  [QuoteType.Cancelation]: 'Cancelation',
  [QuoteType.Endorsement]: 'Endorsement',
  [QuoteType.NewBusiness]: 'New Business',
  [QuoteType.Reinstatement]: 'Reinstatement',
  [QuoteType.Renewal]: 'Renewal',
  [QuoteType.Rewrite]: 'Rewrite',
  [QuoteType.Indication]: 'Indication',
} as const;

/**
 * Display labels for the "Quote Source" field, keyed by the api-types
 * ReportingQuoteSource union so new enum values fail the build until labeled.
 */
export const ReportingQuoteSourceLabels: Record<ReportingQuoteSource, string> = {
  BindingApi: 'Binding API',
  BulkImport: 'Bulk Import',
  CrossSell: 'Cross-Sell',
  CustomUrl: 'Custom URL',
  EmbeddedPosApi: 'Embedded POS API',
  ExDate: 'Ex-Date',
  ExternalEntry: 'External Entry',
  InternalEntry: 'Internal Entry',
  LeadList: 'Lead List',
  SubmissionIngested: 'Submission Ingested',
};

/** Sentinel accepted by the search reportingQuoteSource filters to match records without a value. */
export const REPORTING_QUOTE_SOURCE_NONE_FILTER = 'None';

export enum QuoteSearchQueryParamsKey {
  // CreatedByUser='createdByUserId',
  // PropertyManager='propertyManagerId',
  AllQuotes = '',
  Brokerage = 'brokerageId',
  BrokerageProducer = 'brokerageProducerId',
  BusinessClass = 'productOptionId',
  CreatedBy = 'createdByUserFullName',
  CurrentPage = 'currentPage',
  Customer = 'customerId',
  DuplicateQuotes = 'hasDuplicateQuotes',
  EffectiveOnMax = 'effectiveOnMax',
  EffectiveOnMin = 'effectiveOnMin',
  Jurisdiction = 'jurisdictionId',
  PageLimit = 'pageLimit',
  PageOffset = 'pageOffset',
  PinnedByUser = 'pinnedByUserId',
  Product = 'offeringId',
  ProductLabel = 'productLabelId',
  QuoteType = 'quoteType',
  SearchText = 'searchText',
  Sort = 'sort',
  Status = 'statuses',
  TotalPremiumMax = 'totalPremiumMax',
  TotalPremiumMin = 'totalPremiumMin',
  Underwriter = 'underwriterId',
  UpdatedAtMax = 'updatedAtMax',
  UpdatedAtMin = 'updatedAtMin',
  WatchedByUser = 'watchedByUserId',
  WritingCompany = 'writingCompanyId',
}

export enum MultiPolicyQuoteStatus {
  QuoteFailedBindRelatedQuoteFailedBind,
  QuoteBoundRelatedQuoteFailedBind,
}

export enum QuoteOriginNames {
  Default = 'Default',
  Pickleball = 'Pickleball',
  MGAHub = 'MGA Hub',
  FIAR = 'FIAR',
}

export const QuoteStatus = {
  Bound: {
    id: 'cl42yw1bv0006ugfg3pf3cnww',
    name: 'Bound',
    queryParam: 'Bound',
    externalStatus: 'Bound',
    internalStatus: 'Bound',
  },
  Canceled: {
    id: 'cl42yw1bw0009ugfgg6k85q1j',
    name: 'Canceled',
    queryParam: 'Canceled',
    externalStatus: 'Cancelled',
    internalStatus: 'Cancelled',
  },
  Declined: {
    id: 'cl42yw1bw0007ugfg0needp2n',
    name: 'Declined',
    queryParam: 'Declined',
    externalStatus: 'Declined',
    internalStatus: 'Declined',
  },
  Expired: {
    id: 'cl42yw1bw000augfg9rlfe24e',
    name: 'Expired',
    queryParam: 'Expired',
    externalStatus: 'Lapsed',
    internalStatus: 'Lapsed',
  },
  Rated: {
    id: 'cl42yw1bv0002ugfgbvylc4ji',
    name: 'Rated',
    queryParam: 'Rated',
    externalStatus: 'Quote Incomplete',
    internalStatus: 'Quote Incomplete',
  },
  ReadyForRating: {
    id: 'cl42yw1bv0001ugfgb2k62ogx',
    name: 'Ready for Rating',
    queryParam: 'ReadyForRating',
    externalStatus: 'Submission Incomplete',
    internalStatus: 'Submission Complete',
  },
  ReadyToBind: {
    id: 'cl42yw1bv0005ugfgcwkj8fy0',
    name: 'Ready to Bind',
    queryParam: 'ReadyToBind',
    externalStatus: 'Ready to Bind',
    internalStatus: 'Ready to Bind',
  },
  Referred: {
    id: 'cl42yw1bv0003ugfghu6kdg95',
    name: 'Referred',
    queryParam: 'Referred',
    externalStatus: 'Referred to Underwriting',
    internalStatus: 'Referred to Underwriting',
  },
  Rejected: {
    id: 'cl42yw1bw0008ugfgdzioe92v',
    name: 'Rejected',
    queryParam: 'Rejected',
    externalStatus: 'Rejected',
    internalStatus: 'Rejected',
  },
  Submission: {
    id: 'cl42yw1bv0000ugfg30lde0ig',
    name: 'Submission',
    queryParam: 'Submission',
    externalStatus: 'Submission Incomplete',
    internalStatus: 'Submission Incomplete',
  },
  Underwritten: {
    id: 'cl42yw1bv0004ugfg2ue0dsxk',
    name: 'Underwritten',
    queryParam: 'Underwritten',
    externalStatus: 'Proposal Ready',
    internalStatus: 'Quote Complete',
  },
};

export const quoteStatusInProcess = [
  QuoteStatus.Rated.name,
  QuoteStatus.ReadyForRating.name,
  QuoteStatus.ReadyToBind.name,
  QuoteStatus.Referred.name,
  QuoteStatus.Submission.name,
  QuoteStatus.Underwritten.name,
];

export const quoteStatusTerminal = [
  QuoteStatus.Bound.name,
  QuoteStatus.Rejected.name,
  QuoteStatus.Declined.name,
  QuoteStatus.Canceled.name,
  QuoteStatus.Expired.name,
];

// TODO: Globally replace the use of this enum with the QuoteStatus object above
export enum QuoteStatusId {
  Bound = 'cl42yw1bv0006ugfg3pf3cnww',
  Canceled = 'cl42yw1bw0009ugfgg6k85q1j',
  Declined = 'cl42yw1bw0007ugfg0needp2n',
  Expired = 'cl42yw1bw000augfg9rlfe24e',
  Rated = 'cl42yw1bv0002ugfgbvylc4ji',
  ReadyForRating = 'cl42yw1bv0001ugfgb2k62ogx',
  ReadyToBind = 'cl42yw1bv0005ugfgcwkj8fy0',
  Referred = 'cl42yw1bv0003ugfghu6kdg95',
  Rejected = 'cl42yw1bw0008ugfgdzioe92v',
  Submission = 'cl42yw1bv0000ugfg30lde0ig',
  Underwritten = 'cl42yw1bv0004ugfg2ue0dsxk',
}

export const ExternalQuoteStatus: Record<string, string> = {
  [QuoteStatusId.Bound]: QuoteStatus.Bound.externalStatus,
  [QuoteStatusId.Canceled]: QuoteStatus.Canceled.externalStatus,
  [QuoteStatusId.Declined]: QuoteStatus.Declined.externalStatus,
  [QuoteStatusId.Expired]: QuoteStatus.Expired.externalStatus,
  [QuoteStatusId.Rated]: QuoteStatus.Rated.externalStatus,
  [QuoteStatusId.ReadyForRating]: QuoteStatus.ReadyForRating.externalStatus,
  [QuoteStatusId.ReadyToBind]: QuoteStatus.ReadyToBind.externalStatus,
  [QuoteStatusId.Referred]: QuoteStatus.Referred.externalStatus,
  [QuoteStatusId.Rejected]: QuoteStatus.Rejected.externalStatus,
  [QuoteStatusId.Submission]: QuoteStatus.Submission.externalStatus,
  [QuoteStatusId.Underwritten]: QuoteStatus.Underwritten.externalStatus,
};

export const InternalQuoteStatus: Record<string, string> = {
  [QuoteStatusId.Bound]: QuoteStatus.Bound.internalStatus,
  [QuoteStatusId.Canceled]: QuoteStatus.Canceled.internalStatus,
  [QuoteStatusId.Declined]: QuoteStatus.Declined.internalStatus,
  [QuoteStatusId.Expired]: QuoteStatus.Expired.internalStatus,
  [QuoteStatusId.Rated]: QuoteStatus.Rated.internalStatus,
  [QuoteStatusId.ReadyForRating]: QuoteStatus.ReadyForRating.internalStatus,
  [QuoteStatusId.ReadyToBind]: QuoteStatus.ReadyToBind.internalStatus,
  [QuoteStatusId.Referred]: QuoteStatus.Referred.internalStatus,
  [QuoteStatusId.Rejected]: QuoteStatus.Rejected.internalStatus,
  [QuoteStatusId.Submission]: QuoteStatus.Submission.internalStatus,
  [QuoteStatusId.Underwritten]: QuoteStatus.Underwritten.internalStatus,
};

export enum ColumnType {
  Action = 'actions',
  String = 'string',
  Number = 'number',
  Date = 'date',
  Menu = 'Menu',
}

export interface IOfferingIneligibility {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  offeringId: string;
  ineligibilityId: string;
  effectiveOn: Date;
  effectiveThrough: Date;
}

export interface IIneligibility {
  id: string;
  ineligibilityGroupId: string;
  createdAt: Date;
  updatedAt: Date;
  description: string;
  orderBy: number;
  offeringIneligibilities: IOfferingIneligibility[];
}

export interface IIneligibilityDefault {
  id: string;
  description: string;
}

export interface Exposure {
  checked: boolean;
  description?: string;
  id: string;
  ineligibilityGroup: {
    id: string;
    label: string;
    orderBy?: number;
  };
  orderBy?: number;
}

export interface IneligibilityResponse {
  groupedIneligibility: Record<string, IIneligibility[] | IIneligibilityDefault[]>;
  exposureResponse: Record<string, Exposure>;
}

export enum IneligibilityGroup {
  Risk = 'cl3yn8bly000021fe9trn8am5',
  Property = 'cl3ys68f60000grfe0iti641x',
  GeneralLiability = 'cl3ys68f70001grfe964o0poy',
  Categories = 'clrpmhw0h0000wav52wgnau2c',
  Sports = 'clrpmhw0h0001wav53ni0drih',
}
